import { createHash } from "node:crypto";

import type {
  ExtensionAPI,
  ExtensionCommandContext,
  ExtensionContext,
} from "@earendil-works/pi-coding-agent";
import type { TSchema } from "typebox";

import { Q5MClient } from "./client.js";
import {
  clearAuthentication,
  resolveLoginApiUrl,
  loadConfig,
  saveAuthentication,
  saveContext,
} from "./config.js";
import {
  authorizeWithDeviceFlow,
  openBrowser,
  revokeDeviceCredential,
  type DeviceAuthorization,
} from "./device-auth.js";
import type {
  Catalog,
  JsonObject,
  PeerToolDefinition,
  ToolAnnotations,
  ToolDefinition,
} from "./types.js";

interface ExtensionDependencies {
  loadConfig?: typeof loadConfig;
  saveAuthentication?: typeof saveAuthentication;
  saveContext?: typeof saveContext;
  clearAuthentication?: typeof clearAuthentication;
  openBrowser?: (url: string) => Promise<boolean>;
  sleep?: (milliseconds: number) => Promise<void>;
  now?: () => number;
  env?: NodeJS.ProcessEnv;
  fetch?: typeof globalThis.fetch;
}

interface ExtensionState {
  catalog?: Catalog;
  client?: Q5MClient;
  error?: string;
}

function labelFor(name: string): string {
  return name
    .split("_")
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}


function toolResultText(output: unknown): string {
  return typeof output === "string" ? output : JSON.stringify(output, null, 2);
}

function nativePeerToolName(agent: string, tool: string): string {
  const raw = `q5m_${agent}__${tool}`.replace(/[^a-zA-Z0-9_-]/g, "_");
  if (raw.length <= 64) return raw;
  const suffix = createHash("sha256").update(raw).digest("hex").slice(0, 8);
  return `${raw.slice(0, 55)}_${suffix}`;
}

function peerToolDefinitions(output: unknown): PeerToolDefinition[] {
  if (!Array.isArray(output)) return [];
  return output.filter((candidate): candidate is PeerToolDefinition => {
    if (candidate === null || typeof candidate !== "object") return false;
    return (
      "name" in candidate &&
      typeof candidate.name === "string" &&
      "description" in candidate &&
      typeof candidate.description === "string" &&
      "input_schema" in candidate &&
      candidate.input_schema !== null &&
      typeof candidate.input_schema === "object" &&
      !Array.isArray(candidate.input_schema) &&
      "annotations" in candidate &&
      candidate.annotations !== null &&
      typeof candidate.annotations === "object" &&
      "readOnlyHint" in candidate.annotations &&
      typeof candidate.annotations.readOnlyHint === "boolean" &&
      "destructiveHint" in candidate.annotations &&
      typeof candidate.annotations.destructiveHint === "boolean"
    );
  });
}

async function confirmDestructive(
  annotations: Partial<ToolAnnotations> | undefined,
  label: string,
  context: ExtensionContext,
): Promise<void> {
  if (!annotations?.destructiveHint) return;
  if (!context.hasUI) {
    throw new Error(`Destructive q5m tool requires interactive confirmation: ${label}`);
  }
  const confirmed = await context.ui.confirm("Run destructive q5m tool?", label);
  if (!confirmed) throw new Error(`Cancelled ${label}`);
}

export function createQ5MExtension(dependencies: ExtensionDependencies = {}) {
  return async function q5mExtension(pi: ExtensionAPI): Promise<void> {
    const state: ExtensionState = {};
    const registered = new Set<string>();
    let authenticationInProgress = false;

    const registerPeerTools = (agent: string, definitions: PeerToolDefinition[]): string[] => {
      if (!state.client) return [];
      const enabled: string[] = [];
      for (const definition of definitions) {
        const nativeName = nativePeerToolName(agent, definition.name);
        if (
          !registered.has(nativeName) &&
          pi.getAllTools().some((tool) => tool.name === nativeName)
        ) {
          continue;
        }

        pi.registerTool({
          name: nativeName,
          label: `${agent}: ${labelFor(definition.name)}`,
          description: `[q5m ${agent}] ${definition.description}`,
          parameters: definition.input_schema as TSchema,
          executionMode: definition.annotations?.readOnlyHint ? "parallel" : "sequential",
          async execute(_toolCallId, params, signal, _onUpdate, context) {
            const currentDefinition = await refreshPeerDefinition(
              agent,
              definition.name,
              signal,
            );
            await confirmDestructive(
              currentDefinition.annotations,
              `${agent}/${definition.name}`,
              context,
            );
            const client = state.client;
            if (!client) throw new Error(state.error ?? "q5m is not connected");
            const output = await client.callTool(
              "call_tool",
              {
                agent,
                name: definition.name,
                arguments: params as JsonObject,
              },
              signal,
            );
            return {
              content: [{ type: "text", text: toolResultText(output) }],
              details: { output, agent, tool: definition.name },
            };
          },
        });
        registered.add(nativeName);
        enabled.push(nativeName);
      }

      if (enabled.length > 0) {
        pi.setActiveTools([...new Set([...pi.getActiveTools(), ...enabled])]);
      }
      return enabled;
    };

    const refreshPeerDefinition = async (
      agent: string,
      name: string,
      signal: AbortSignal | undefined,
    ): Promise<PeerToolDefinition> => {
      const client = state.client;
      if (!client) throw new Error(state.error ?? "q5m is not connected");
      const output = await client.callTool("list_tools", { agent }, signal);
      const definitions = peerToolDefinitions(output);
      registerPeerTools(agent, definitions);
      const definition = definitions.find((candidate) => candidate.name === name);
      if (
        !definition ||
        typeof definition.annotations?.destructiveHint !== "boolean"
      ) {
        throw new Error(`Cannot safely classify q5m peer tool: ${agent}/${name}`);
      }
      return definition;
    };


    const refreshCatalogDefinition = async (
      name: string,
      signal: AbortSignal | undefined,
    ): Promise<ToolDefinition> => {
      const client = state.client;
      if (!client) throw new Error(state.error ?? "q5m is not connected");
      const catalog = await client.getCatalog(signal);
      state.catalog = catalog;
      const current = catalog.tools.find((candidate) => candidate.name === name);
      if (
        !current ||
        typeof current.annotations?.destructiveHint !== "boolean"
      ) {
        throw new Error(`Cannot safely classify q5m tool: ${name}`);
      }
      return current;
    };
    const registerCatalogTool = (definition: ToolDefinition): void => {
      // Re-register on /q5m reload so a backend schema change takes effect
      // without restarting Pi. Pi replaces same-name extension tools atomically.
      pi.registerTool({
        name: definition.name,
        label: `q5m: ${labelFor(definition.name)}`,
        description: definition.description,
        parameters: definition.input_schema as TSchema,
        executionMode: definition.annotations.readOnlyHint ? "parallel" : "sequential",
        async execute(_toolCallId, params, signal, _onUpdate, context) {
          const arguments_ = params as JsonObject;
          const client = state.client;
          if (!client) throw new Error(state.error ?? "q5m is not connected");
          const peerName =
            definition.name === "call_tool" && typeof arguments_.name === "string"
              ? arguments_.name
              : undefined;
          const peerAgent =
            definition.name === "call_tool" && typeof arguments_.agent === "string"
              ? arguments_.agent
              : undefined;
          const currentDefinition = await refreshCatalogDefinition(definition.name, signal);
          let annotations: Partial<ToolAnnotations> | undefined =
            currentDefinition.annotations;
          if (peerName && peerAgent) {
            const peerDefinition = await refreshPeerDefinition(peerAgent, peerName, signal);
            annotations = {
              ...currentDefinition.annotations,
              destructiveHint: peerDefinition.annotations?.destructiveHint,
            };
          }
          await confirmDestructive(
            annotations,
            peerName ? `${peerAgent ?? "peer"}/${peerName}` : definition.name,
            context,
          );
          const output = await client.callTool(definition.name, arguments_, signal);
          let enabledTools: string[] = [];
          if (definition.name === "list_tools" && typeof arguments_.agent === "string") {
            enabledTools = registerPeerTools(
              arguments_.agent,
              peerToolDefinitions(output),
            );
          }
          const enabledNote =
            enabledTools.length > 0
              ? `\n\nThe client enabled native tools: ${enabledTools.join(", ")}. Prefer these exact-schema tools for the next call.`
              : "";
          return {
            content: [{ type: "text", text: `${toolResultText(output)}${enabledNote}` }],
            details: { output, enabledTools },
          };
        },
      });
      registered.add(definition.name);
    };

    const initialize = async (): Promise<void> => {
      try {
        const config = await (dependencies.loadConfig ?? loadConfig)();
        if (!config.apiKey) {
          throw new Error("Run /q5m login, then use /q5m reload.");
        }
        const client = new Q5MClient({
          apiUrl: config.apiUrl,
          apiKey: config.apiKey,
          ...(config.groupId ? { groupId: config.groupId } : {}),
          ...(dependencies.fetch ? { fetch: dependencies.fetch } : {}),
        });
        const catalog = await client.getCatalog();
        state.client = client;
        state.catalog = catalog;
        state.error = undefined;
        for (const tool of catalog.tools) registerCatalogTool(tool);
      } catch (error) {
        state.catalog = undefined;
        state.client = undefined;
        state.error = error instanceof Error ? error.message : String(error);
      }
    };

    const disableOwnedTools = (): void => {
      const owned = new Set(registered);
      pi.setActiveTools(pi.getActiveTools().filter((name) => !owned.has(name)));
    };

    const enableCatalogTools = (): void => {
      if (!state.catalog) return;
      pi.setActiveTools([
        ...new Set([...pi.getActiveTools(), ...state.catalog.tools.map((tool) => tool.name)]),
      ]);
    };

    const reloadConnection = async (): Promise<void> => {
      disableOwnedTools();
      await initialize();
      enableCatalogTools();
    };

    const connectionMessage = (): string =>
      state.catalog
        ? `q5m connected to ${state.client?.apiUrl} (${state.catalog.tools.length} tools, ${state.catalog.context} Home, ${state.catalog.commit})`
        : `q5m not connected: ${state.error ?? "unknown error"}`;

    const authenticate = async (
      onVerification: (authorization: DeviceAuthorization) => void | Promise<void>,
      signal?: AbortSignal,
    ): Promise<Catalog> => {
      if (authenticationInProgress) {
        throw new Error("q5m login is already in progress");
      }
      const environment = dependencies.env ?? process.env;
      if (environment.Q5M_API_KEY?.trim()) {
        throw new Error(
          "Unset Q5M_API_KEY before q5m login; environment credentials override saved extension authentication.",
        );
      }

      authenticationInProgress = true;
      try {
        const apiUrl = resolveLoginApiUrl(environment);
        const current = await (dependencies.loadConfig ?? loadConfig)();
        const apiKey = await authorizeWithDeviceFlow({
          apiUrl,
          clientId: "q5m-agent-extension",
          ...(dependencies.fetch ? { fetch: dependencies.fetch } : {}),
          ...(dependencies.sleep ? { sleep: dependencies.sleep } : {}),
          ...(dependencies.now ? { now: dependencies.now } : {}),
          ...(signal ? { signal } : {}),
          onVerification,
        });
        await (dependencies.saveAuthentication ?? saveAuthentication)(apiUrl, apiKey);
        const client = new Q5MClient({
          apiUrl,
          apiKey,
          ...(current.groupId ? { groupId: current.groupId } : {}),
          ...(dependencies.fetch ? { fetch: dependencies.fetch } : {}),
        });
        const catalog = await client.getCatalog(signal);
        await (dependencies.saveContext ?? saveContext)(catalog.context);
        await reloadConnection();
        if (!state.catalog) {
          throw new Error(state.error ?? "q5m tools did not reload after login");
        }
        return state.catalog;
      } finally {
        authenticationInProgress = false;
      }
    };

    const login = async (context: ExtensionCommandContext): Promise<void> => {
      if (!context.hasUI) return;
      if (context.mode !== "tui") {
        context.ui.notify(
          "q5m login requires an interactive Pi or OMP TUI. Start interactively or run 'q5m auth login'.",
          "error",
        );
        return;
      }
      try {
        await authenticate(async (authorization) => {
          context.ui.notify(
            `Open ${authorization.verification_uri}\nEnter code: ${authorization.user_code}`,
            "info",
          );
          const opened = await (dependencies.openBrowser ?? openBrowser)(
            authorization.verification_uri_complete ?? authorization.verification_uri,
          );
          if (!opened) {
            context.ui.notify("Open the URL above to finish signing in.", "warning");
          }
        });
        context.ui.notify(connectionMessage(), state.catalog ? "info" : "error");
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        context.ui.notify(`q5m login failed: ${message}`, "error");
      }
    };

    const logout = async (context: ExtensionCommandContext): Promise<void> => {
      try {
        const config = await (dependencies.loadConfig ?? loadConfig)();
        if (config.credentialSource === "config" && config.apiKey) {
          await revokeDeviceCredential(
            config.apiUrl,
            config.apiKey,
            dependencies.fetch ?? globalThis.fetch,
          );
        }
        await (dependencies.clearAuthentication ?? clearAuthentication)();
        const environmentKeyActive = Boolean(
          (dependencies.env ?? process.env).Q5M_API_KEY?.trim(),
        );
        await reloadConnection();
        if (environmentKeyActive) {
          if (context.hasUI) {
            context.ui.notify(
              "Saved q5m credential removed, but Q5M_API_KEY is still active in the environment.",
              "warning",
            );
          }
          return;
        }
        if (context.hasUI) context.ui.notify("Logged out of q5m.", "info");
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (context.hasUI) context.ui.notify(`q5m logout failed: ${message}`, "error");
      }
    };

    const loginTool: Parameters<ExtensionAPI["registerTool"]>[0] & {
      approval: "exec";
    } = {
      name: "q5m_login",
      label: "Sign in to q5m",
      description:
        "Start browser device authorization for q5m after the user explicitly asks to sign in. " +
        "Shows a short confirmation code, opens the browser when possible, and activates q5m tools after approval.",
      parameters: {
        type: "object",
        properties: {},
        additionalProperties: false,
      } as TSchema,
      executionMode: "sequential",
      approval: "exec",
      async execute(_toolCallId, _params, signal, onUpdate, context) {
        if (!context.hasUI) {
          throw new Error("q5m login requires interactive approval");
        }
        const confirmed = await context.ui.confirm(
          "Connect q5m?",
          "Open a browser and connect this Pi or OMP session to your q5m account.",
        );
        if (!confirmed) throw new Error("q5m login cancelled");
        const catalog = await authenticate(async (authorization) => {
          onUpdate?.({
            content: [
              {
                type: "text",
                text:
                  `Open ${authorization.verification_uri}\n` +
                  `Confirm code: ${authorization.user_code}`,
              },
            ],
            details: {
              status: "authorization_pending",
              verificationUri: authorization.verification_uri,
              userCode: authorization.user_code,
            },
          });
          await (dependencies.openBrowser ?? openBrowser)(
            authorization.verification_uri_complete ?? authorization.verification_uri,
          );
        }, signal);
        return {
          content: [
            {
              type: "text",
              text: `Connected to q5m. ${catalog.tools.length} tools are active in the ${catalog.context} Home.`,
            },
          ],
          details: {
            connected: true,
            context: catalog.context,
            commit: catalog.commit,
            toolCount: catalog.tools.length,
          },
        };
      },
    };
    pi.registerTool(loginTool);

    pi.registerCommand("q5m", {
      description: "Manage the native q5m connection",
      handler: async (args, context) => {
        const normalized = args.trim();
        const [action = "status", argument, ...extra] = normalized
          ? normalized.split(/\s+/u)
          : ["status"];
        if (action === "login" && argument === undefined && extra.length === 0) {
          await login(context);
          return;
        }
        if (action === "logout" && argument === undefined) {
          await logout(context);
          return;
        }
        if (action === "reload" && argument === undefined) {
          await reloadConnection();
        } else if (
          action !== "status" ||
          argument !== undefined ||
          extra.length > 0
        ) {
          if (context.hasUI) {
            context.ui.notify(
              "Usage: /q5m [status|reload|login|logout]",
              "warning",
            );
          }
          return;
        }

        if (context.hasUI) {
          context.ui.notify(connectionMessage(), state.catalog ? "info" : "error");
        }
      },
    });

    pi.on("before_agent_start", (event) => {
      if (!state.catalog) return;
      const directGuidance =
        "This Pi or OMP session has the q5m agent network mounted as native tools over the direct q5m HTTP gateway. " +
        "No MCP transport is involved. list_tools automatically enables exact-schema q5m_<agent>__<tool> tools; prefer an enabled native peer tool over wrapping the same call in call_tool.";
      return {
        systemPrompt: `${event.systemPrompt}\n\n## q5m agent network\n\n${state.catalog.instructions}\n\n${directGuidance}`,
      };
    });

    await initialize();
  };
}

export default createQ5MExtension();
